import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { listMessages, PaginatedMessagesResponse } from "@/services/messagesApi";

export default function MessagesPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<PaginatedMessagesResponse | null>(null);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [senderFilter, setSenderFilter] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await listMessages(navigate, { page, size, sender: senderFilter || undefined });
        setData(res);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erreur inconnue");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, page, size, senderFilter]);

  const canPrev = page > 1;
  const canNext = data ? page < data.pages : false;

  const senderBadge = (sender: string) => {
    if (sender === "CANDIDATE") return <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Candidat</span>;
    if (sender === "RECRUITER") return <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Recruteur</span>;
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">{sender}</span>;
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Messages</h1>
          </div>
        </header>

        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Messages LinkedIn</h2>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Expediteur</label>
                <select className="border rounded px-2 py-1 text-sm" value={senderFilter} onChange={(e) => { setPage(1); setSenderFilter(e.target.value); }}>
                  <option value="">Tous</option>
                  <option value="CANDIDATE">Candidat</option>
                  <option value="RECRUITER">Recruteur</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Taille page</label>
                <select className="border rounded px-2 py-1 text-sm" value={size} onChange={(e) => { setPage(1); setSize(parseInt(e.target.value, 10)); }}>
                  {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>
          </div>

          {error && <div className="text-red-600 text-sm">{error}</div>}
          {loading && <div className="text-gray-500 text-sm">Chargement...</div>}

          <div className="overflow-auto border rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">ID</th>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Expediteur</th>
                  <th className="px-3 py-2 text-left">Candidat</th>
                  <th className="px-3 py-2 text-left">Subscription</th>
                  <th className="px-3 py-2 text-left">Message</th>
                </tr>
              </thead>
              <tbody>
                {data?.items?.map((m) => (
                  <tr key={m.id} className="border-t">
                    <td className="px-3 py-2">{m.id}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{new Date(m.at).toLocaleString()}</td>
                    <td className="px-3 py-2">{senderBadge(m.sender)}</td>
                    <td className="px-3 py-2">{m.candidate_name || `#${m.candidate_id}`}</td>
                    <td className="px-3 py-2">{m.subscription_id}</td>
                    <td className="px-3 py-2 max-w-md truncate" title={m.message}>{m.message}</td>
                  </tr>
                ))}
                {!data?.items?.length && !loading && (
                  <tr><td className="px-3 py-4 text-center text-gray-500" colSpan={6}>Aucun message</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">Page {data?.page ?? page} / {data?.pages ?? 1} — {data?.total ?? 0} elements</div>
            <div className="flex gap-2">
              <Button variant="outline" disabled={!canPrev} onClick={() => setPage((p) => Math.max(1, p - 1))}>Precedent</Button>
              <Button variant="outline" disabled={!canNext} onClick={() => setPage((p) => p + 1)}>Suivant</Button>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
